import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Webhook,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookEventType,
} from "../../types/webhook";

interface WebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook | null;
  eventTypes: Record<string, WebhookEventType>;
  onSubmit: (data: CreateWebhookRequest | UpdateWebhookRequest) => void;
  isLoading: boolean;
}

const WebhookModal: React.FC<WebhookModalProps> = ({
  isOpen,
  onClose,
  webhook,
  eventTypes,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation(["webhooks", "common"]);

  const [formData, setFormData] = useState<
    CreateWebhookRequest & {
      event_type_name: string;
      event_type_description: string;
    }
  >({
    name: "",
    description: "",
    event_type: "",
    event_type_name: "",
    event_type_description: "",
    is_active: true,
    timeout: 10,
    retry_count: 3,
    retry_delay: 60,
  });

  useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name,
        description: webhook.description,
        event_type: webhook.event_type,
        event_type_name: eventTypes[webhook.event_type]?.name || "",
        event_type_description:
          eventTypes[webhook.event_type]?.description || "",
        is_active: webhook.is_active,
        timeout: webhook.timeout,
        retry_count: webhook.retry_count,
        retry_delay: webhook.retry_delay,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        event_type: "",
        event_type_name: "",
        event_type_description: "",
        is_active: true,
        timeout: 10,
        retry_count: 3,
        retry_delay: 60,
      });
    }
  }, [webhook, eventTypes]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Приоритет: выбранный event_type (ключ), иначе — ручной ввод (кастомный ключ)
    const submitData = {
      ...formData,
      event_type: formData.event_type || formData.event_type_name,
    };

    // Удаляем временные поля перед отправкой
    const { event_type_name, event_type_description, ...finalData } =
      submitData;

    onSubmit(finalData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {webhook
              ? t("webhooks:edit_webhook")
              : t("webhooks:create_webhook")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:name")} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:description")}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Event Type (Select from known types) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:table.event_type")} *
            </label>
            <select
              value={formData.event_type || ""}
              onChange={(e) => {
                const nextType = e.target.value;
                handleInputChange("event_type", nextType);
                handleInputChange(
                  "event_type_description",
                  eventTypes[nextType]?.description || ""
                );
                // Не подставляем "человеческое" название в event_type_name, чтобы оно
                // случайно не уехало в payload вместо ключа события.
                handleInputChange("event_type_name", "");
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t("common:select")}</option>
              {Object.entries(eventTypes).map(([key, meta]) => (
                <option key={key} value={key}>
                  {key}
                  {meta?.name ? ` — ${meta.name}` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Можно выбрать из списка или указать своё значение ниже.
            </p>
          </div>

          {/* Event Type Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:event_type_name")}
            </label>
            <input
              type="text"
              value={formData.event_type_name}
              onChange={(e) =>
                handleInputChange("event_type_name", e.target.value)
              }
              placeholder="Кастомный ключ (например: game_user_updated)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Event Type Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:event_type_description")}
            </label>
            <textarea
              value={formData.event_type_description}
              onChange={(e) =>
                handleInputChange("event_type_description", e.target.value)
              }
              placeholder="Описание события, например: Событие срабатывает при регистрации нового пользователя"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  handleInputChange("is_active", e.target.checked)
                }
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("webhooks:is_active")}
              </span>
            </label>
          </div>

          {/* Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:timeout")} (секунды)
            </label>
            <input
              type="number"
              value={formData.timeout}
              onChange={(e) =>
                handleInputChange("timeout", parseInt(e.target.value))
              }
              min="1"
              max="300"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Retry Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:retry_count")}
            </label>
            <input
              type="number"
              value={formData.retry_count}
              onChange={(e) =>
                handleInputChange("retry_count", parseInt(e.target.value))
              }
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Retry Delay */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:retry_delay")} (секунды)
            </label>
            <input
              type="number"
              value={formData.retry_delay}
              onChange={(e) =>
                handleInputChange("retry_delay", parseInt(e.target.value))
              }
              min="1"
              max="3600"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 transition-colors"
            >
              {t("common:cancel")}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading
                ? t("common:loading")
                : webhook
                ? t("common:update")
                : t("common:create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebhookModal;
